import React, { useState } from 'react';
import { forgotPassword } from '../../../modules/users';
import { useNotification } from '../../../hooks/NotificationContext';
import TableActionsMenu from '../../elements/TableActionsMenu';
import ConfirmationModal from '../../elements/ConfirmationModal';
import { resendLinkToUser } from '../../../lib/utils';
import { modifyCustomerStatus } from '../../../modules/admin';

const CustomerTableActions = ({ row, fetchCustomerList }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const {organization_id: organizationId, customer} = row;

  const user = row?.org_admin || {};
  const { status, platform, full_name, email, id } = user;

  const handleOpenConfirm = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
  };
  const handleCloseConfirm = () => setShowConfirm(false);

  const handleSubmit = async () => {
    if (!confirmAction) return;

    setLoading(true);
    try {
      let response;

      if (confirmAction === 'resetPassword') {
        await forgotPassword({ email });
        addNotification('success', 'Password reset link sent successfully!');
      } else if (confirmAction === 'resendConfirmationEmail') {
        await handleResendLink(email);
      } else {
        response = await modifyCustomerStatus({
          organizationId,
          type: confirmAction,
        });
        fetchCustomerList();
        addNotification(
          'success',
          response?.message || 'Customer updated successfully!'
        );
      }

    } catch (err) {
      addNotification('danger', err.message);
    } finally {
      setLoading(false);
      handleCloseConfirm();
    }
  };

  const resetPasswordConfirmationBody =
    platform === 'web'
      ? `Are you sure you want to reset the password for ${full_name}? 
        A link will be sent to their registered email to reset their password.`
      : `This user signed up using an external authentication method and cannot reset their password manually.`;

  const menuOptionsConfig = {
    resetPassword: {
      label: 'Reset Admin Password',
      visible: status === 'active',
      action: () => handleOpenConfirm('resetPassword'),
    },
    deactivate: {
      label: 'Deactivate Customer',
      visible: status === 'active' || status === 'pending',
      action: () => handleOpenConfirm('deactivate'),
    },
    activate: {
      label: 'Activate Customer',
      visible: status === 'inactive' || status === 'suspended',
      action: () => handleOpenConfirm('activate'),
    },
    manageSubscription: {
      label: 'Manage Subscription',
      visible: status !== 'delete-requested',
      action: () => (window.location.href = `/subscriptions/${id}`),
      disabled: true,
    },
    cancelDeletion: {
      label: 'Cancel Deletion',
      visible: status === 'delete-requested',
      action: () => handleOpenConfirm('cancelDeletion'),
    },
    resendConfirmationEmail: {
      label: 'Resend Confirmation Email',
      visible: status === 'pending',
      action: () => handleOpenConfirm('resendConfirmationEmail'),
    },
  };

  const menuOptions = Object.values(menuOptionsConfig)
    .filter((option) => option.visible)
    .map(({ label, action, disabled }) => ({
      label,
      icon: '',
      onClick: action,
      disabled,
    }));

  const confirmationMessages = {
    resetPassword: resetPasswordConfirmationBody,
    activate: `Are you sure you want to activate ${customer}? This will allow the organization's users to access the platform again.`,
    deactivate: `Are you sure you want to deactivate ${customer}? This will prevent the organization's users from accessing the platform. You can reactivate them at any time.`,
    cancelDeletion: `Are you sure you want to cancel the account deletion request for ${customer}? This will re-activate the customer?`,
    resendConfirmationEmail: `The org admin has not verified their email. Are you sure you want to resend the confirmation email to ${full_name}?`,
  };

  const handleResendLink = async (userEmail) => {
    try {
      const response = await resendLinkToUser(userEmail, 'confirmation');
      addNotification('success', response);
    } catch (error) {
      addNotification('error', error.message);
    }
  };

  return (
    <>
      <TableActionsMenu options={menuOptions} />

      <ConfirmationModal
        show={showConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleSubmit}
        title={
          confirmAction
            ? confirmAction
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim()
            : ''
        }
        body={confirmationMessages[confirmAction] || ''}
        buttonLabel={loading ? 'Please wait...' : 'Confirm'}
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
        loading={loading}
        hideButton={confirmAction === 'resetPassword' && platform !== 'web'}
      />
    </>
  );
};

export default CustomerTableActions;
